import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import { DELETE_EXPENSE, EDIT_EXPENSE } from "../../constants";
import { CardLayout, DeleteModal, MTable } from "../v3";
import { getExpenseTableColumns } from "../../pages/expense/constants";
import { FinanceService } from "../../services/finance.service";
import { derivePdfColumns, getPrintableRows, useHasPermission } from "../../utils";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

const DEFAULT_SORT = [{ field: "date", sort: "desc" as const }];

export const OfficeExpense = () => {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
    dropdowns,
    setDropdowns,
  } = usePersistedTableState({
    tableId: "office-expenses",
    defaults: { dropdowns: { categoryId: "" }, sortModel: DEFAULT_SORT },
  });
  const selectedCategoryId = dropdowns.categoryId;
  const setSelectedCategoryId = (categoryId) => setDropdowns({ categoryId });
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    expenseId: null,
  });

  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const hasEditExpense = useHasPermission(EDIT_EXPENSE);
  const hasDeleteExpense = useHasPermission(DELETE_EXPENSE);

  const fetchCategories = useCallback(async () => {
    try {
      const categoryResponse = await FinanceService.getCategories({ limit: 100 });
      setCategories(categoryResponse?.data?.rows || []);
    } catch (error) {
      console.error("OfficeExpense ~ fetchCategories error:", error);
    }
  }, []);

  const fetchData = useCallback(
    async (expenseFilters = {}) => {
      setLoading(true);

      try {
        const categoryIds = categories?.map((item) => item.id).join(",");

        if (categories.length > 0) {
          const expenseResponse = await FinanceService.getExpenses({
            ...expenseFilters,
            categoryIds: selectedCategoryId ? selectedCategoryId : categoryIds,
            isOffice: true,
          });

          setExpenses(expenseResponse?.data?.rows || []);
          setTotalCount(expenseResponse?.data?.count || 0);
        }
      } catch (error) {
        toast.error("Failed to fetch office expenses");
        console.error("OfficeExpense ~ fetchData error:", error);
      } finally {
        setLoading(false);
      }
    },
    [selectedCategoryId, categories],
  );

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (!dateRange) return;

    fetchData({
      sortBy: sortModel[0]?.field,
      sortOrder: sortModel[0]?.sort,
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      description: searchText,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
    });
  }, [fetchData, sortModel, paginationState, searchText, dateRange]);

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing title="Office Expenses" columnHeaders={pdfColumnHeaders} tableRows={printableRows} company={user?.company} />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: "Office Expenses",
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleCategoryFilterChange = (value) => {
    setSelectedCategoryId(value ?? "");
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleCategoryFilterClear = () => {
    setSelectedCategoryId("");
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const onViewClick = (params) => {
    // TODO: Wire office expense detail route when backend support is available.
    navigate(`/expense/${params.row.id}`);
  };

  const openDeleteModal = (expense) => {
    setDeleteModal({
      isOpen: true,
      expenseId: expense?.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      expenseId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await FinanceService.deleteExpense(deleteModal.expenseId);
      toast.success("Expense deleted successfully");
      fetchData({
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        page: paginationState.page + 1,
        limit: paginationState.pageSize,
        description: searchText,
        fromDate: dateRange?.startDate,
        toDate: dateRange?.endDate,
      });
    } catch (error) {
      toast.error("Failed to delete expense");
    } finally {
      closeDeleteModal();
    }
  };

  const handleActions = (expenseData, actionType) => {
    switch (actionType) {
      case "edit":
        navigate(`/expense/edit/${expenseData.id}?accountType=office&tab=expense`);
        break;
      case "delete":
        openDeleteModal(expenseData);
        break;
      default:
        return;
    }
  };

  const columnHeaders = useMemo(
    () => getExpenseTableColumns(handleActions, hasEditExpense, hasDeleteExpense, true),
    [hasEditExpense, hasDeleteExpense],
  );

  const dropdownFilters = [
    {
      key: "categoryId",
      label: "Category",
      value: selectedCategoryId,
      onChange: handleCategoryFilterChange,
      onClear: handleCategoryFilterClear,
      options: categories.map((category) => ({ label: category.name, value: category.id })),
      multiple: false,
    },
  ];

  const rows = expenses.map((expense, index) => ({
    sl: index + 1 + paginationState.page * paginationState.pageSize,
    id: expense.id,
    date: moment(expense.date).format("DD MMM YYYY"),
    description: expense.description || "",
    amount: Number(expense.amount),
    type: categories.find((c: any) => c.id === expense.categoryId)?.name || "-",
  }));

  return (
    <>
      <CardLayout>
        <MTable
          dropdownFilters={dropdownFilters}
          placeholder="Search by description"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          showDateRangePicker
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          showExportButton
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount}
          loading={loading}
          sortModel={sortModel}
          defaultSortModel={DEFAULT_SORT}
          onSortChange={setSortModel}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this expense?"
      />
    </>
  );
};
